import { useTenant } from '@/hooks/useTenant';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getCurrentDomain } from '@/utils/domain';
import { useTranslation } from 'react-i18next';

export default function TestTenantPage() {
  const tenant = useTenant();
  const domain = getCurrentDomain();
  const { t } = useTranslation();

  return (
    <div className="container mx-auto p-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8">
        {t('dev.testTenant.title', 'Tenant System Test Page')}
      </h1>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('dev.testTenant.domainTitle', 'Domain Information')}</CardTitle>
            <CardDescription>
              {t('dev.testTenant.domainDescription', 'Current domain resolution details')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">
                {t('dev.testTenant.hostname', 'Hostname:')}
              </span>
              <code className="text-sm bg-muted px-2 py-1 rounded">{domain.hostname}</code>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">
                {t('dev.testTenant.subdomain', 'Subdomain:')}
              </span>
              <code className="text-sm bg-muted px-2 py-1 rounded">
                {domain.subdomain || t('common.none', 'None')}
              </code>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">
                {t('dev.testTenant.isCustomDomain', 'Is Custom Domain:')}
              </span>
              <Badge variant={domain.isCustomDomain ? "default" : "secondary"}>
                {domain.isCustomDomain ? t('common.yes', 'Yes') : t('common.no', 'No')}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">
                {t('dev.testTenant.isLocalhost', 'Is Localhost:')}
              </span>
              <Badge variant={domain.isLocalhost ? "default" : "secondary"}>
                {domain.isLocalhost ? t('common.yes', 'Yes') : t('common.no', 'No')}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">
                {t('dev.testTenant.baseDomain', 'Base Domain:')}
              </span>
              <code className="text-sm bg-muted px-2 py-1 rounded">{domain.baseDomain}</code>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dev.testTenant.providerTitle', 'Provider Information')}</CardTitle>
            <CardDescription>
              {t('dev.testTenant.providerDescription', 'Resolved provider details from domain')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {tenant.provider ? (
              <>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t('dev.testTenant.providerId', 'Provider ID:')}
                  </span>
                  <code className="text-sm bg-muted px-2 py-1 rounded">{tenant.provider.id}</code>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t('dev.testTenant.providerName', 'Provider Name:')}
                  </span>
                  <span>{tenant.provider.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t('dev.testTenant.providerSlug', 'Provider Slug:')}
                  </span>
                  <code className="text-sm bg-muted px-2 py-1 rounded">{tenant.provider.slug}</code>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t('dev.testTenant.providerDomain', 'Provider Domain:')}
                  </span>
                  <code className="text-sm bg-muted px-2 py-1 rounded">{tenant.provider.domain}</code>
                </div>
                <div>
                  <span className="font-medium">
                    {t('dev.testTenant.customDomains', 'Custom Domains:')}
                  </span>
                  <div className="mt-1 space-y-1">
                    {tenant.provider.customDomains.length > 0 ? (
                      tenant.provider.customDomains.map((domain) => (
                        <code key={domain} className="block text-sm bg-muted px-2 py-1 rounded">
                          {domain}
                        </code>
                      ))
                    ) : (
                      <span className="text-muted-foreground">
                        {t('common.none', 'None')}
                      </span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">
                {t('dev.testTenant.noProvider', 'No provider loaded')}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dev.testTenant.brandingTitle', 'Branding Information')}</CardTitle>
            <CardDescription>
              {t('dev.testTenant.brandingDescription', 'Provider theming and customization')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {tenant.branding ? (
              <>
                <div className="flex justify-between items-center">
                  <span className="font-medium">
                    {t('dev.testTenant.primaryColor', 'Primary Color:')}
                  </span>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {tenant.branding.primaryColor || t('common.defaultValue', 'default')}
                    </code>
                    {tenant.branding.primaryColor && (
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: tenant.branding.primaryColor }}
                      />
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">
                    {t('dev.testTenant.secondaryColor', 'Secondary Color:')}
                  </span>
                  <div className="flex items-center gap-2">
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {tenant.branding.secondaryColor || t('common.defaultValue', 'default')}
                    </code>
                    {tenant.branding.secondaryColor && (
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: tenant.branding.secondaryColor }}
                      />
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t('dev.testTenant.theme', 'Theme:')}
                  </span>
                  <Badge>{tenant.branding.theme || t('common.defaultValue', 'default')}</Badge>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">
                    {t('dev.testTenant.logo', 'Logo:')}
                  </span>
                  <code className="text-sm bg-muted px-2 py-1 rounded">
                    {tenant.branding.logo || t('common.defaultValue', 'default')}
                  </code>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">
                {t('dev.testTenant.noBranding', 'No branding configured')}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dev.testTenant.quickTestsTitle', 'Quick Tests')}</CardTitle>
            <CardDescription>
              {t('dev.testTenant.quickTestsDescription', 'Test different providers and functionality')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">
                {t('dev.testTenant.providerLinksHeading', 'Test Different Providers:')}
              </h4>
              <div className="flex gap-2 flex-wrap">
                {['demo', 'acme', 'digitalstorm'].map((slug) => (
                  <a
                    key={slug}
                    href={`https://${slug}.app:3000/test-tenant`}
                    className="text-sm text-primary hover:underline"
                  >
                    {`${slug}.app`}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">
                {t('dev.testTenant.refreshHeading', 'Refresh Tenant Data:')}
              </h4>
              <button
                onClick={() => tenant.refreshTenant()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                {t('dev.testTenant.refreshButton', 'Refresh Provider')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
